// SportsBannerMain.jsx
import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useHistory } from 'react-router-dom';

/*
  Full component with caching, abortable fetch, and concurrency-limited odds requests.
  Drop this file in place of your existing SportsBannerMain implementation.
*/

/* -------------------- CACHING & HELPERS (module scope) -------------------- */
const marketsCacheKey = 'sports_markets_cache_v1';
const marketsCacheTTL = 10 * 1000; // 10s - tune as needed
const oddsCacheTTL = 8 * 1000; // 8s - tune as needed

const marketsCache = {
  data: null,
  ts: 0
};

// odds cache: Map<marketId, {ts:number, value:object}>
const oddsCache = new Map();

function setMarketsCache(data) {
  marketsCache.data = data;
  marketsCache.ts = Date.now();
  try {
    localStorage.setItem(marketsCacheKey, JSON.stringify({ ts: marketsCache.ts, data }));
  } catch (e) { /* ignore storage errors */ }
}

function loadMarketsCacheFromStorage() {
  try {
    const raw = localStorage.getItem(marketsCacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.ts && (Date.now() - parsed.ts) < marketsCacheTTL) {
      marketsCache.data = parsed.data;
      marketsCache.ts = parsed.ts;
      return parsed.data;
    }
  } catch (e) { /* ignore parse errors */ }
  return null;
}

function getCachedOdds(marketId) {
  const entry = oddsCache.get(marketId);
  if (!entry) return null;
  if ((Date.now() - entry.ts) > oddsCacheTTL) {
    oddsCache.delete(marketId);
    return null;
  }
  return entry.value;
}
function setCachedOdds(marketId, value) {
  oddsCache.set(marketId, { ts: Date.now(), value });
}

/* Simple concurrency helper: process array in batches (size = concurrency) */
async function mapWithConcurrency(items, mapper, concurrency = 6) {
  const results = new Array(items.length);
  let idx = 0;

  async function worker() {
    while (true) {
      const current = idx++;
      if (current >= items.length) return;
      try {
        results[current] = await mapper(items[current], current);
      } catch (err) {
        results[current] = null;
      }
    }
  }

  const workers = new Array(Math.min(concurrency, items.length))
    .fill(0)
    .map(() => worker());

  await Promise.all(workers);
  return results;
}

/* -------------------- Component -------------------- */
const SportsBannerMain = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useRef(null);
  const history = useHistory();
  const maxScroll = useRef(0);

  const sportsData = [
    { id: 1, name: 'In Play', icon: '⚡️', tab: 'all-sports' },
    { id: 2, name: 'Cricket', icon: '🏏', tab: 'cricket' },
    { id: 3, name: 'Soccer', icon: '⚽', tab: 'soccer' },
    { id: 4, name: 'Tennis', icon: '🎾', tab: 'tennis' },
    { id: 6, name: "Golf", icon: "⛳", tab: 'golf' },
    { id: 7, name: "Boxing", icon: "👊", tab: 'boxing' },
    { id: 8, name: "American Football", icon: "🏈", tab: 'american-football' },
    { id: 9, name: "Baseball", icon: "⚾", tab: 'baseball' },
    { id: 10, name: "Basketball", icon: "🏀", tab: 'basketball' },
    { id: 11, name: "Rugby League", icon: "🏉", tab: 'rugby-league' },
  ];

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateMaxScroll = () => {
      const containerWidth = container.offsetWidth;
      const scrollContent = container.querySelector('[data-scroll-track]');
      if (scrollContent) {
        const scrollWidth = scrollContent.scrollWidth;
        maxScroll.current = Math.max(0, scrollWidth - containerWidth);
      }
    };
    updateMaxScroll();
    window.addEventListener('resize', updateMaxScroll);

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const scrollSensitivity = 1;
      const delta = (e.deltaX !== 0 ? e.deltaX : e.deltaY) * scrollSensitivity;

      setScrollPosition((prev) => {
        const newPosition = prev + delta;
        return Math.max(0, Math.min(newPosition, maxScroll.current || 0));
      });
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      window.removeEventListener('resize', updateMaxScroll);
    };
  }, []);

  const [isLoading, setIsLoading] = useState(true);
  const [mobileMatches, setMobileMatches] = useState([]);
  const [activeTab, setActiveTab] = useState('in-play');
  const [sporttab, setSportTab] = useState('all-sports');
  const [isSmallScreen, setIsSmallScreen] = useState(typeof window !== 'undefined' ? window.innerWidth <= 480 : true);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleSportTabClick = (tabValue) => {
    setIsLoading(true);
    setSportTab(tabValue);
  };

  /* -------------------- abortable, cached fetchMatchOdds -------------------- */
  const fetchMatchOdds = async (matchId, signal) => {
    try {
      // 1) check in-memory cache
      const cached = getCachedOdds(matchId);
      if (cached) return cached;

      const res = await fetch(
        `https://bkk07.com/sports/market-odds?market_id=${matchId}`,
        { headers: { 'Content-Type': 'application/json' }, signal }
      );

      if (!res.ok) {
        // handle HTTP error gracefully
        console.warn('Odds fetch failed', res.status);
        return null;
      }
      const json = await res.json();
      const [marketOdds] = json;
      if (!marketOdds?.runners) return null;

      const runners = marketOdds.runners;
      const formatted = {
        one: { back: '--', lay: '--' },
        x: { back: '--', lay: '--' },
        two: { back: '--', lay: '--' }
      };

      if (runners.length === 2) {
        formatted.one = {
          back: runners[0].ex.availableToBack[0]?.price || '--',
          lay: runners[0].ex.availableToLay[0]?.price || '--'
        };
        formatted.two = {
          back: runners[1].ex.availableToBack[0]?.price || '--',
          lay: runners[1].ex.availableToLay[0]?.price || '--'
        };
      } else if (runners.length === 3) {
        formatted.one = {
          back: runners[0].ex.availableToBack[0]?.price || '--',
          lay: runners[0].ex.availableToLay[0]?.price || '--'
        };
        formatted.x = {
          back: runners[1].ex.availableToBack[0]?.price || '--',
          lay: runners[1].ex.availableToLay[0]?.price || '--'
        };
        formatted.two = {
          back: runners[2].ex.availableToBack[0]?.price || '--',
          lay: runners[2].ex.availableToLay[0]?.price || '--'
        };
      }

      // save to cache
      setCachedOdds(matchId, formatted);
      return formatted;
    } catch (err) {
      if (err.name === 'AbortError') {
        // expected when user rapidly switches tabs - ignore
        return null;
      }
      console.error('Error fetching odds:', err);
      return null;
    }
  };

  /* -------------------- fetchAllMatches with caching + concurrency -------------------- */
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let didCancel = false;

    const tabToSportId = {
      'cricket': '4',
      'tennis': '2',
      'soccer': '1',
      'american-football': '6423',
      'baseball': '7511',
      'basketball': '7522',
      'boxing': '6',
      'darts': '3503',
      'gaelic-games': '2152880',
      'golf': '3',
      'greyhound-racing': '4339',
      'horse-racing': '7',
      'mma': '26420387',
      'motorsport': '8',
      'rugby-league': '1477',
      'rugby-union': '5',
      'snooker': '6422'
    };

    const fetchAllMatches = async () => {
      setIsLoading(true);
      try {
        // try load from in-memory or localStorage cache first
        const cachedMarkets = (marketsCache.data && (Date.now() - marketsCache.ts) < marketsCacheTTL)
          ? marketsCache.data
          : loadMarketsCacheFromStorage();

        let rawMarkets = [];

        if (cachedMarkets && sporttab === 'all-sports' && activeTab === 'in-play') {
          // Use cached markets when valid
          rawMarkets = cachedMarkets;
        } else {
          if (activeTab === 'in-play') {
            if (sporttab === 'all-sports') {
              const [inPlayRes, allMatchesRes] = await Promise.all([
                fetch('https://bkk07.com/sports/allinplay', { headers: { 'Content-Type': 'application/json' }, signal }),
                fetch('https://bkk07.com/sports/all-matches', { headers: { 'Content-Type': 'application/json' }, signal })
              ]);

              const inPlayData = inPlayRes.ok ? await inPlayRes.json() : [];
              const allMatchesData = allMatchesRes.ok ? await allMatchesRes.json() : [];

              const collectMarkets = (arr) => Array.isArray(arr) ? arr.flatMap(s => s?.markets || []) : [];

              const inPlayMarkets = collectMarkets(inPlayData).map(m => ({ ...m, inplay: true }));
              const allMarkets = collectMarkets(allMatchesData);

              const map = new Map();
              inPlayMarkets.forEach(m => m?.marketId && map.set(m.marketId, m));
              allMarkets.forEach(m => {
                if (m?.marketId && !map.has(m.marketId)) map.set(m.marketId, m);
              });

              rawMarkets = Array.from(map.values());
              // cache markets for short TTL
              setMarketsCache(rawMarkets);
            } else {
              const sportId = tabToSportId[sporttab];
              if (sportId) {
                const res = await fetch(`https://bkk07.com/sports/inplayGameId/${sportId}`, { headers: { 'Content-Type': 'application/json' }, signal });
                const json = res.ok ? await res.json() : [];
                const games = json?.games || json || [];
                rawMarkets = Array.isArray(games) ? games.flatMap(g => g?.markets || []) : [];
              } else {
                rawMarkets = [];
              }
            }
          } else {
            // If non in-play tab behavior needed later, add here (currently your code focuses on in-play)
            rawMarkets = [];
          }
        }

        if (didCancel) return;

        // Build UI matches and fetch odds — skip odds fetch when cached
        // Use concurrency-limited mapping to reduce simultaneous fetches
        const withOdds = await mapWithConcurrency(rawMarkets, async (m) => {
          const utcDate = new Date(m.marketStartTime);
          const timeStr = new Intl.DateTimeFormat('en-IN', {
            hour: '2-digit', minute: '2-digit', hour12: true
          }).format(utcDate);

          const match = {
            id: m.marketId,
            runners: m.runners,
            team1: m.runners[0]?.runnerName || '',
            team2: m.runners[1]?.runnerName || '',
            status: (m?.inplay ? 'In-Play' : timeStr),
            isLive: Boolean(m?.inplay),
            time: timeStr,
            date: utcDate,
            isSuspended: m.stopBet,
            eventid: m.version,
            odds: { one: { back: '--', lay: '--' }, x: { back: '--', lay: '--' }, two: { back: '--', lay: '--' } }
          };

          // Reuse cached odds if available
          const cached = getCachedOdds(match.id);
          if (cached) {
            match.odds = cached;
            return match;
          }

          // otherwise fetch odds with the same abort signal
          const odds = await fetchMatchOdds(match.id, signal);
          if (odds) match.odds = odds;
          return match;
        }, 6); // concurrency = 6

        if (didCancel) return;

        // sort: in-play first, then by start time
        withOdds.sort((a, b) => {
          if (a.isLive && !b.isLive) return -1;
          if (!a.isLive && b.isLive) return 1;
          return new Date(a.date) - new Date(b.date);
        });

        setMobileMatches(withOdds);
      } catch (err) {
        if (err.name === 'AbortError') {
          // aborted — ignore
        } else {
          console.error('Error fetching matches:', err);
        }
      } finally {
        if (!didCancel) setIsLoading(false);
      }
    };

    fetchAllMatches();

    return () => {
      didCancel = true;
      controller.abort();
    };
  }, [sporttab, activeTab]);

  /* -------------------- UI helpers & rendering -------------------- */

  const mobileMatchesMap = (mobileMatches || []).map((data) => ({
    id: data?.id,
    team1: data?.team1,
    team2: data?.team2,
    status: data?.status,
    isLive: data?.isLive,
    date: data?.date || new Date(),
    isSuspended: data?.isSuspended,
    competition: 'International',
    eventId: data.eventid,
    odds: data.odds || {
      one: { back: '--', lay: '--' },
      x: { back: '--', lay: '--' },
      two: { back: '--', lay: '--' }
    }
  }));

  const getMatchStatus = (match) => (
    <Status status={match.status}>
      {match.status}
    </Status>
  );

  const handleMatchClick = (matchId) => {
    const selectedMatch = mobileMatches?.find(match => match.id === matchId);
    history.push({
      pathname: `/sports/markets/${matchId}`,
      state: {
        matchData: {
          ...selectedMatch,
          matchId: matchId,
          eventId: selectedMatch?.eventid,
          matchTitle: `${selectedMatch?.team1} v ${selectedMatch?.team2}`,
          competition: selectedMatch?.competition,
          status: selectedMatch?.status,
          isLive: selectedMatch?.isLive,
          isSuspended: selectedMatch?.isSuspended
        }
      }
    });
  };

  const bannerContainerStyle = {
    width: '100%',
    padding: '10px 6px',
    overflow: 'hidden',
    margintop: '20px',
  };

  const scrollContainerStyle = {
    position: 'relative',
    height: '80px',
    cursor: 'grab',
    userSelect: 'none',
  };

  const scrollTrackStyle = {
    display: 'flex',
    gap: '25px',
    transform: `translateX(-${scrollPosition}px)`,
    transition: 'transform 0.1s ease-out',
    width: 'max-content',
    willChange: 'transform',
  };

  const sportCardStyle = {
    flexShrink: 0,
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: '15px',
    padding: '4px 40px',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  };

  const sportCardHoverStyle = {
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'scale(1.05)',
  };

  const sportContentStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    whiteSpace: 'nowrap',
  };

  const sportIconStyle = {
    fontSize: '20px',
    transition: 'transform 0.3s ease',
  };

  const sportNameStyle = {
    color: 'white',
    fontWeight: '500',
    fontFamily: 'Montserrat Alternates, sans-serif',
    fontSize: '14px',
  };

  const progressFillStyle = {
    height: '100%',
    background: 'rgba(255, 255, 255, 0.6)',
    transition: 'width 0.1s ease',
    width: `${maxScroll.current > 0 ? Math.min(100, (scrollPosition / maxScroll.current) * 100) : 0}%`,
  };

  return (
    <>
      <div style={bannerContainerStyle}>
        <div
          ref={containerRef}
          style={scrollContainerStyle}
          onMouseDown={(e) => (e.currentTarget.style.cursor = 'grabbing')}
          onMouseUp={(e) => (e.currentTarget.style.cursor = 'grab')}
          onMouseLeave={(e) => (e.currentTarget.style.cursor = 'grab')}
        >
          <div style={scrollTrackStyle} data-scroll-track>
            {sportsData.map((sport) => {
              const isActive = sporttab === sport.tab;
              const activeStyle = isActive ? {
                background: 'linear-gradient(135deg, #25ee89, rgba(129, 105, 25, 0.22))',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 20px rgba(125, 84, 14, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                position: 'relative',
                overflow: 'hidden',
              } : {};

              return (
                <div
                  key={sport.id}
                  style={{ ...sportCardStyle, ...activeStyle }}
                  onClick={() => handleSportTabClick(sport.tab)}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      Object.assign(e.currentTarget.style, {
                        ...sportCardStyle,
                        ...sportCardHoverStyle
                      });
                    }
                    const icon = e.currentTarget.querySelector('.sport-icon');
                    if (icon) icon.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      Object.assign(e.currentTarget.style, sportCardStyle);
                    } else {
                      Object.assign(e.currentTarget.style, {
                        ...sportCardStyle,
                        ...activeStyle
                      });
                    }
                    const icon = e.currentTarget.querySelector('.sport-icon');
                    if (icon) icon.style.transform = isActive ? 'scale(1.05)' : 'scale(1)';
                  }}
                >
                  <div style={sportContentStyle}>
                    <span className="sport-icon" style={{
                      ...sportIconStyle,
                      transform: isActive ? 'scale(1.15)' : 'scale(1)',
                      filter: isActive ? 'drop-shadow(0 2px 8px #25ee89) brightness(1)' : 'none'
                    }}>
                      {sport.icon}
                    </span>
                    <span style={{
                      ...sportNameStyle,
                      color: isActive ? '#fff' : 'white',
                      fontWeight: isActive ? '700' : '500',
                      textShadow: isActive ? '0 2px 8px #25ee89' : 'none',
                      letterSpacing: isActive ? '0.5px' : 'normal'
                    }}>
                      {sport.name}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <style jsx>{`
          @keyframes pulse {
            0%,100% { opacity: 0.6; } 50% { opacity: 1; }
          }
          @keyframes shimmer {
            0% { left: -100%; } 100% { left: 100%; }
          }
          @keyframes glow {
            0%,100% { box-shadow: 0 8px 25px rgba(240, 156, 6, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2); }
            50% { box-shadow: 0 12px 35px rgba(240, 156, 6, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3); }
          }
        `}</style>
      </div>

      <TabContent>
        {isLoading ? (
          Array.from({ length: isSmallScreen ? 6 : 8 }).map((_, idx) => (
            <SkeletonRow key={idx}>
              <SkeletonLeft>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <SkeletonDot />
                  <SkeletonLine widthPercent={70} heightPx={isSmallScreen ? 10 : 12} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SkeletonPill widthPx={isSmallScreen ? 40 : 56} heightPx={isSmallScreen ? 16 : 20} />
                  <SkeletonPill widthPx={isSmallScreen ? 16 : 20} heightPx={isSmallScreen ? 16 : 20} />
                  <SkeletonPill widthPx={isSmallScreen ? 16 : 20} heightPx={isSmallScreen ? 16 : 20} />
                  <SkeletonPill widthPx={isSmallScreen ? 16 : 20} heightPx={isSmallScreen ? 16 : 20} />
                  <SkeletonPill widthPx={isSmallScreen ? 16 : 20} heightPx={isSmallScreen ? 16 : 20} />
                  <SkeletonLine widthPercent={20} heightPx={isSmallScreen ? 10 : 12} style={{ marginLeft: 8 }} />
                </div>
              </SkeletonLeft>
              <SkeletonOddsGroup>
                <SkeletonOddsBox mobile={isSmallScreen} wide />
                <SkeletonOddsBox mobile={isSmallScreen} wide />
                <SkeletonOddsBox mobile={isSmallScreen} wide />
                <SkeletonOddsBox mobile={isSmallScreen} wide />
              </SkeletonOddsGroup>
            </SkeletonRow>
          ))
        ) : mobileMatchesMap.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            textAlign: 'center',
            color: '#fff',
            fontFamily: 'Montserrat Alternates, sans-serif',
            fontSize: '16px'
          }}>
            <img
              src="/assets/images/bcgames/affiliate/empty.webp"
              style={{
                width: '90px',
                height: '90px',
                marginBottom: '10px',
                objectFit: 'contain'
              }}
              alt="warning icon"
            />
            <span>Oops! , No match found for this right now .</span>
          </div>
        ) : (
          mobileMatchesMap.map((match, index) => (
            <Match key={index}>
              <MatchInfo>
                <div style={{ display: 'flex', gap: '0px', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <StatusDot isLive={match?.status} />
                      <MatchTitle onClick={() => handleMatchClick(match.id)}>
                        {`${match.team1} v ${match.team2}`}
                      </MatchTitle>
                      {getMatchStatus(match)}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <IconGroup>
                        {
                          ['cricket'].includes(sporttab) ? (
                            <img
                              src='/assets/images/sports/cricket.png'
                              style={{ width: '30px', height: '20px', objectFit: 'contain' }}
                              alt="cricket"
                            />
                          ) : sporttab === 'tennis' ? (
                            <img
                              src='/assets/images/sports/tennis.png'
                              style={{ width: '30px', height: '20px', objectFit: 'contain' }}
                              alt="tennis"
                            />
                          ) : sporttab === 'soccer' ? (
                            <img
                              src='/assets/images/sports/soccer.png'
                              style={{ width: '30px', height: '20px', objectFit: 'contain' }}
                              alt="soccer"
                            />
                          ) : null
                        }

                        <img
                          src="/assets/images/sports/livestream.png"
                          style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                          alt="livestream icon"
                        />
                        <img
                          src="/assets/images/sports/watch.png"
                          style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                          alt="watch icon"
                        />
                        <img
                          src="/assets/images/sports/fancy.png"
                          style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                          alt="fancy icon"
                        />
                        <img
                          src="/assets/images/sports/book.png"
                          style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                          alt="book icon"
                        />
                      </IconGroup>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <div style={{
                        backgroundColor: '#72C0F4',
                        padding: '6px 4px',
                        width: "66px",
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                        minWidth: '20px',
                        textAlign: 'center'
                      }}>
                        {match?.odds?.one?.back}
                      </div>

                      <div style={{
                        backgroundColor: '#FFB6C1',
                        padding: '6px 4px',
                        width: "66px",
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                        minWidth: '20px',
                        textAlign: 'center'
                      }}>
                        {match?.odds?.one?.lay}
                      </div>

                      <div style={{
                        backgroundColor: '#72C0F4',
                        padding: '6px 4px',
                        width: "66px",
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                        minWidth: '20px',
                        textAlign: 'center'
                      }}>
                        {match?.odds?.x?.back}
                      </div>

                      <div style={{
                        backgroundColor: '#FFB6C1',
                        padding: '6px 4px',
                        width: "66px",
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                        minWidth: '20px',
                        textAlign: 'center'
                      }}>
                        {match?.odds?.x?.lay}
                      </div>

                      <div style={{
                        backgroundColor: '#72C0F4',
                        padding: '6px 4px',
                        width: "66px",
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                        minWidth: '20px',
                        textAlign: 'center'
                      }}>
                        {match?.odds?.two?.back}
                      </div>

                      <div style={{
                        backgroundColor: '#FFB6C1',
                        padding: '6px 4px',
                        width: "66px",
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'black',
                        minWidth: '20px',
                        textAlign: 'center'
                      }}>
                        {match?.odds?.two?.lay}
                      </div>
                    </div>
                  </div>
                </div>
              </MatchInfo>
            </Match>
          ))
        )}
      </TabContent>
    </>
  );
};

/* -------------------- Styled components (unchanged, same as your original) -------------------- */

const Match = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid rgb(50, 55, 56);
  background: #292d2e;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const MatchInfo = styled.div`
  flex: 1;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const MatchTitle = styled.div`
  color: #ffffff;
  font-weight: bold;
  margin-bottom: 0px;
  cursor: pointer;
  display: inline-block;
  font-size: 14px;
  font-family: 'Montserrat Alternates', sans-serif;

  &:hover {
    text-decoration: underline;
    color: rgb(240, 156, 6);
  }
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: ${(props) => (props.isLive === 'In-Play' ? '#2ecc71' : '#95a5a6')};
  border-radius: 50%;
  margin-right: 8px;
  margin-top: 2px;
  animation: ${(props) => (props.isLive === 'In-Play' ? 'blink 1s ease-in-out infinite' : 'none')};

  @keyframes blink {
    0% { opacity: 0; } 50% { opacity: 1; } 100% { opacity: 0; }
  }
`;

const Status = styled.div`
  color: ${(props) => {
    switch (props.status) {
      case 'In-Play':
        return 'rgb(240, 156, 6)';
      case 'Not Started':
        return '#cccccc';
      case 'Suspended':
        return '#e74c3c';
      default:
        return '#cccccc';
    }
  }};
  font-size: 14px;
  font-weight: bold;
  font-family: 'Montserrat Alternates', sans-serif;
`;

const IconGroup = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: -5px;
  font-family: 'Montserrat Alternates', sans-serif;
`;

/* Skeleton styles */
const shimmerAnim = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.15), rgba(255,255,255,0.05));
  background-size: 200% 100%;
  animation: ${shimmerAnim} 1.2s ease-in-out infinite;
`;

const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid rgb(50, 55, 56);
  background: #292d2e;
`;

const SkeletonLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SkeletonDot = styled(SkeletonBase)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
`;

const SkeletonLine = styled(SkeletonBase)`
  width: ${(p) => (p.widthPercent ? `${p.widthPercent}%` : '60%')};
  height: ${(p) => (p.heightPx ? `${p.heightPx}px` : '12px')};
  border-radius: 6px;
`;

const SkeletonPill = styled(SkeletonBase)`
  width: ${(p) => (p.widthPx ? `${p.widthPx}px` : '40px')};
  height: ${(p) => (p.heightPx ? `${p.heightPx}px` : '16px')};
  border-radius: 10px;
`;

const SkeletonOddsGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
`;

const SkeletonOddsBox = styled(SkeletonBase)`
  width: ${(p) => (p.wide ? (p.mobile ? '60px' : '90px') : p.mobile ? '30px' : '36px')};
  height: ${(p) => (p.mobile ? '22px' : '28px')};
  border-radius: 8px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 150px;
  background: #292d2e;
`;

const Loader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const LoaderSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgb(240, 156, 6);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }
  }
`;

const LoaderText = styled.div`
  color: rgb(240, 156, 6);
  font-size: 14px;
  font-weight: 500;
  animation: pulse-text 1.5s ease-in-out infinite;

  @keyframes pulse-text {
    0%, 100% { opacity: 0.6; } 50% { opacity: 1; }
  }
`;

const TabContent = styled.div`
  margin-top: -30px;
  animation: fadeIn 0.3s ease;
  max-height: 270px;
  overflow-y: auto;
  background-color: #292d2e;
  font-family: 'Montserrat Alternates', sans-serif;
  margin-bottom: 20px;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: rgb(50, 55, 56); }
  &::-webkit-scrollbar-thumb { background: rgb(240, 156, 6); border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: rgb(220, 136, 6); }

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

export default SportsBannerMain;
